import React, {
  SyntheticEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import MuiAccordion, { AccordionProps as MuiAccordionProps } from '@mui/material/Accordion';
import { useAccordionState } from './AccordionState';
import { BpDesignSystem } from '../../types';
import { css, styled } from '../../styles';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { mergeClassNames } from '../../utils/cssClassNameHelper';

export type AccordionProps = BpDesignSystem.PropsWithChildren<
  MuiAccordionProps & {
    noBorder?: boolean;
    prominent?: boolean;
    scrollIntoView?: boolean | ScrollIntoViewOptions;
  }
>;

export const Accordion = forwardRef<HTMLDivElement | null, AccordionProps>(
  (
    {
      dataTest = 'accordion',
      onChange,
      defaultExpanded,
      expanded,
      noBorder,
      scrollIntoView,
      className,
      prominent,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null);

    // tie innerRef to ref
    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    const { setState, unmount, isOpen, controlled } = useAccordionState();
    const [isDefaultExpanded] = useState(defaultExpanded);
    // control "open" prop manually if AccordionManager is used
    const isExpanded = controlled
      ? innerRef.current
        ? isOpen(innerRef.current)
        : (expanded ?? defaultExpanded ?? false)
      : expanded;

    // Update AccordionManager state ( if Provider does not exist - setState being mocked )
    // If AccordionManager is used, AccordionManager will handle the
    // "open" prop. If AccordionManager is not used, Accordion will
    // handle the "open" prop.
    useEffect(() => {
      const ref = innerRef.current;
      if (ref) {
        setState(ref, { open: expanded ?? isDefaultExpanded ?? false });
      }
      return () => {
        if (ref) {
          unmount(ref);
        }
      };
    }, [setState, expanded, isDefaultExpanded, unmount]);

    const handleOnChange = useCallback(
      (event: SyntheticEvent, expanded: boolean) => {
        if (innerRef.current) {
          setState(innerRef.current, { open: expanded });
          if (scrollIntoView && expanded) {
            setTimeout(() => {
              innerRef.current?.scrollIntoView?.(
                typeof scrollIntoView === 'boolean' ? { block: 'nearest', behavior: 'smooth' } : scrollIntoView
              );
            }, 300);
          }
        }
        onChange?.(event, expanded);
      },
      [onChange, setState, scrollIntoView]
    );

    return (
      <StyledAccordion
        data-test={dataTest}
        aria-expanded={isExpanded}
        onChange={handleOnChange}
        className={mergeClassNames(className, {
          'BpCard-prominent': prominent
        })}
        {...props}
        expanded={isExpanded}
        defaultExpanded={!controlled ? isDefaultExpanded : undefined}
        ref={innerRef}
        $noBorder={noBorder}
      />
    );
  }
);

const topBorderCss = css`
  // Only want top border displayed if Details have content. Doing here
  // as want the border to display straight away. If done conditionally
  // when panel is expanded its display can lag behind the transition.
  .MuiCollapse-wrapper [role='region']:not(:empty) {
    border-top: 1px solid ${tokens.colorSurfaceVariant};
  }
`;

const StyledAccordion = styled(MuiAccordion)<{ $noBorder?: boolean }>`
  && {
    margin: 0 !important;

    .MuiCollapse-hidden {
      border-top: none;
    }

    ${({ $noBorder }) => (!$noBorder ? topBorderCss : '')}
  }

  &:before {
    height: 0 !important;
  }
`;
